import { useState, useCallback, useMemo } from 'react';
import CustomersModelMc from '../../../models/CustomersModelMc';

/**
 * Hook para manejo de acciones de direcciones de cliente.
 *
 * @hook useCustomerAddressActions
 * @category Hook - Actions Layer
 * @description
 * - Gestiona el guardado y actualización de direcciones de cliente.
 * - Valida y procesa datos del formulario antes de enviar.
 * - Maneja estados de loading y errores durante operaciones.
 * - Integrado con CustomersModelMc para persistencia.
 * - Soporta direcciones temporales y notificaciones opcionales.
 *
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.formMethods - Métodos del formulario de react-hook-form
 * @param {Function} params.updateList - Función para actualizar la lista de direcciones
 * @param {Object} params.customer - Objeto customer con web_id
 * @param {Object} params.selectedAddress - Dirección seleccionada para editar (opcional)
 * @param {boolean} params.fakeAddress - Si es una dirección temporal (no persistida)
 * @param {boolean} params.hideNotification - Si ocultar notificaciones toast
 * @param {Function} params.setErrorMain - Función para setear error de dirección principal
 *
 * @returns {Object} Estado y métodos de acciones
 * @returns {boolean} returns.loading - Estado de loading durante guardado
 * @returns {Function} returns.handleSaveButtonClick - Handler para guardar dirección
 *
 * @example
 * const { loading, handleSaveButtonClick } = useCustomerAddressActions({
 *   formMethods,
 *   updateList,
 *   customer,
 *   selectedAddress,
 *   fakeAddress: false,
 *   hideNotification: false,
 *   setErrorMain
 * });
 *
 * @since 4.0.0
 * @version 4.0.0 - Hook de actions layer para ManageCustomerAddressMC
 */
const useCustomerAddressActions = ({
  formMethods,
  updateList,
  customer,
  selectedAddress,
  fakeAddress,
  hideNotification,
  setErrorMain,
  handleClickOnClose,
}) => {
  const [loading, setLoading] = useState(false);

  const { handleSubmit } = formMethods;

  // Handler para guardar dirección
  const handleSave = useCallback(async (data) => {
    try {
      setLoading(true);
      setErrorMain(false);

      const formData = {
        address: {
          ...data,
          country_id: data.selected_country || null
        }
      };

      delete formData.address.selected_country;

      if (selectedAddress) {
        formData.id = selectedAddress.id;
      }

      // Si es dirección temporal, solo actualizar localmente
      if (fakeAddress) {
        updateList?.(formData.address);
        return;
      }

      // Guardar en el servidor
      const response = await CustomersModelMc.createCustomerAddress(customer.web_id, formData);

      updateList?.(response.address);

      if (!hideNotification && typeof showtoast === 'function') {
        showtoast(response.message);
      }

      handleClickOnClose?.();
    } catch (error) {
      console.error('Error saving address:', error);

      const { status, message } = error;
      const { main } = message || {};

      if (main && main.length > 0) {
        setErrorMain(true);
      }

      if (status !== 422 && !hideNotification && typeof showtoast === 'function') {
        showtoast('Hubo errores al guardar la dirección');
      }
    } finally {
      setLoading(false);
    }
  }, [
    customer,
    fakeAddress,
    hideNotification,
    selectedAddress,
    setErrorMain,
    updateList,
    handleClickOnClose
  ]);

  const handleSaveButtonClick = useMemo(() => handleSubmit(handleSave), [handleSave, handleSubmit]);

  return useMemo(() => ({
    loading,
    handleSaveButtonClick
  }), [loading, handleSaveButtonClick]);
};

export default useCustomerAddressActions;
